import { Component, EventEmitter, Output, OnInit, Input, inject } from "@angular/core";
import { IdNameCombination } from "@models/generic/IdNameCombination";
import { SampleType } from "../../shared/enums/sample-type.enum";
import { AuditSampleModel } from "./audit-sample.model";
import { AuditSamplerDataService } from "./adv-eff-audit-sampler-data.service";
import { BsModalRef } from "ngx-bootstrap/modal";
import { FormGroup, Validators, FormControl, AbstractControl, ReactiveFormsModule, FormsModule } from "@angular/forms";
import { getFormControl } from "@methods/jeffs-toolkit";
import { FormValidationHelper } from "@app/shared/helpers/form-validation-helper";
import { finalize } from "rxjs/operators";
import { toDate } from "../../shared/adv-eff-shared-methods";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/controls/i18n/scope";
import { NgSelectComponent } from "@ng-select/ng-select";
import { DateInputComponent } from "@app/shared/components/date-input/date-input.component";
import { InputTooltipDirective } from "@app/shared/directives/input-tooltip.directive";

/** A failed form validation: a key of the controls scope, rendered with | t. */
type AuditSamplerValidationError = { key: string };

@Component({
    selector: "adv-eff-audit-sampler",
    templateUrl: "./adv-eff-audit-sampler.component.html",
    styleUrls: ["./adv-eff-audit-sampler.component.scss"],
    viewProviders: [provideTranslationScope(scope)],
    imports: [
        ReactiveFormsModule,
        FormsModule,
        NgSelectComponent,
        DateInputComponent,
        TranslatePipe,
        InputTooltipDirective,
    ],
})
export class AuditSamplerComponent implements OnInit {
    private ds = inject(AuditSamplerDataService);
    private _translation = injectTranslation(scope);

    @Input() modalRef: BsModalRef;
    @Output() reload = new EventEmitter();

    sampleModel: AuditSampleModel;
    formGroup: FormGroup;
    generatedSamples: string;

    sampleTypes: IdNameCombination[];
    sampleTypeEnum = SampleType;

    allValidations: AuditSamplerValidationError[] = [];

    isGenerating = false;

    async ngOnInit() {
        await this._translation.ready();

        this.sampleTypes = [
            { ID: SampleType.number, Name: this._translation.t("auditSampler.sampleTypeNumber") },
            { ID: SampleType.date, Name: this._translation.t("auditSampler.sampleTypeDate") },
        ];

        this.sampleModel = new AuditSampleModel();
        this.sampleModel.sampleType = SampleType.number;
        this.formGroup = this.createGroup();
    }

    createGroup(): FormGroup {
        return new FormGroup(
            {
                seedNumber: new FormControl(this.sampleModel.seedNumber, [Validators.required]),
                numberOfSamples: new FormControl(this.sampleModel.numberOfSamples, [
                    Validators.required,
                    Validators.min(1),
                ]),

                testPopulationSize: new FormControl(this.sampleModel.testPopulationSize),
                startDate: new FormControl(this.sampleModel.startDate),
                endDate: new FormControl(this.sampleModel.endDate),
            },
            {
                validators: [(group: FormGroup) => this.validate(group)],
            }
        );
    }

    private validate(group: FormGroup) {
        const testPopulationSizeControl = getFormControl<AuditSampleModel>(
            group,
            (sample) => sample.testPopulationSize
        );
        const startDateControl = getFormControl<AuditSampleModel>(
            group,
            (sample) => sample.startDate
        );
        const endDateControl = getFormControl<AuditSampleModel>(group, (sample) => sample.endDate);

        // Reset all
        startDateControl.setErrors(null);
        endDateControl.setErrors(null);
        testPopulationSizeControl.setErrors(null);
        this.allValidations = [];

        if (this.sampleModel.sampleType === SampleType.number) {
            this.handleNumberValidation(testPopulationSizeControl);
        } else if (this.sampleModel.sampleType === SampleType.date) {
            this.handleDateValidation(startDateControl, endDateControl);
        }

        return {};
    }

    private handleNumberValidation(testPopulationSizeControl: AbstractControl) {
        const testPopulationSize = testPopulationSizeControl.value as number;
        if (testPopulationSizeControl.touched && testPopulationSize <= 0) {
            const minimumValidation = { key: "auditSampler.validationTestPopSizeMin" };
            this.allValidations.push(minimumValidation);
            testPopulationSizeControl.setErrors({ minimum: minimumValidation });
        }
    }

    private handleDateValidation(
        startDateControl: AbstractControl,
        endDateControl: AbstractControl
    ) {
        const startDate = toDate(startDateControl.value);
        const endDate = toDate(endDateControl.value);

        if (startDateControl.touched && (!startDate || startDate <= 0)) {
            const startValidation = { key: "auditSampler.validationStartDateRequired" };
            this.allValidations.push(startValidation);
            startDateControl.setErrors({ required: startValidation });
        } else if (endDateControl.touched && (!endDate || endDate <= 0)) {
            const endValidation = { key: "auditSampler.validationEndDateRequired" };
            this.allValidations.push(endValidation);
            endDateControl.setErrors({ required: endValidation });
        } else if (startDateControl.touched && endDateControl.touched && startDate > endDate) {
            const endAfterStartValidation = { key: "auditSampler.validationEndDateAfterStart" };
            this.allValidations.push(endAfterStartValidation);
            startDateControl.setErrors({ startAfterEnd: { key: "auditSampler.validationStartDateBeforeEnd" } });
            endDateControl.setErrors({ endBeforeStart: endAfterStartValidation });
        }
    }

    generate() {
        FormValidationHelper.markAllAsTouched(this.formGroup);
        this.formGroup.updateValueAndValidity();
        FormValidationHelper.mapToModel(this.formGroup, this.sampleModel);

        if (!this.formGroup.valid) {
            return;
        }

        this.isGenerating = true;
        this.ds
            .calculateSample(this.sampleModel)
            .pipe(finalize(() => (this.isGenerating = false)))
            .subscribe((x) => {
                this.generatedSamples = x.join(
                    this.sampleModel.sampleType === SampleType.number ? "; " : "\n"
                );
            });
    }
}
